import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Pagination from "react-bootstrap/Pagination";
import { getTitleCase } from "@utils/utils";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import commaFormat from "@utils/commaFormat";
import EmptyStateTradeOverview from "./EmptyStateTradeOverview";

const Locations = ({ fetchTradeOverview, companyName, pathname, dataSource, userActivePlan, loader, activeCategData, profileData, activeCategory, expandedItems, activePanelId, headings, lastPage }) => {
  const countDetails = useSelector((state) => state.companyProfile.countDetails);
  const category_count = countDetails[activeCategData?.countEnum];
  //these states are for pagination
  const [currentPage, setCurrentPage] = useState(lastPage);
  const itemsPerPage = 10;
  const [paginatedData, setPaginatedData] = useState([]);
  const totalPages = Math.ceil(category_count / itemsPerPage);

  useEffect(() => {
    if (profileData[activeCategory]) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPaginatedData(profileData[activeCategory].slice(startIndex, endIndex));
    }
  }, [currentPage, profileData, activeCategory]);

  const handlePageChange = async (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationForwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));
      if (currentPage * itemsPerPage >= profileData[activeCategory].length) {
        await fetchTradeOverview(currentPage);
      }
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationBackwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));
      setCurrentPage(currentPage - 1);
    }
  };

  const renderPagination = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, category_count);
    const showPagination = !(currentPage * itemsPerPage >= category_count && currentPage === 1);

    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
        <span>
          Showing {startItem}-{endItem} of
        </span>
        <span className="tw-ml-1">
          <Pagination>
            <Pagination.Item className="page-item pagination-item">{category_count}</Pagination.Item>
            {showPagination && (
              <>
                <Pagination.Prev disabled={currentPage === 1} className="page-item pagination-prev" onClick={() => handlePageChange("prev")} />
                <Pagination.Next disabled={currentPage * itemsPerPage >= category_count} className="page-item pagination-next" onClick={() => handlePageChange("next")} />
              </>
            )}
          </Pagination>
        </span>
      </div>
    );
  };

  return loader ? (
    <LoadingSkeleton lines={6} lineHeight="35px" width="100%" shape="rectangle" />
  ) : paginatedData?.length > 0 ? (
    <div className="tw-space-y-2">
      <span className="tw-font-medium">{activePanelId === "importers" && activeCategory === "location" ? "Importing Addresses of " + companyName : activePanelId === "exporters" && activeCategory === "location" ? "Exporting Addresses of " + companyName : ""}</span>
      {paginatedData.map((item) => {
        const activeHeading = headings.find((heading) => heading.id === activeCategData?.id && activePanelId === heading.activePanelId);
        return (
          item.key &&
          item[activeCategData?.key] && (
            <div key={item.s_no} className={`tw-flex tw-flex-col tw-font-semibold ${!expandedItems.includes(`${item.s_no}-${activePanelId}-${activeCategData.id}`) ? "tw-overflow-hidden" : "tw-bg-gray-100"} tw-border tw-rounded-lg tw-px-4 tw-py-3`}>
              {activeHeading && (
                <div className="tw-flex">
                  <div className="tw-flex tw-flex-col tw-gap-1 tw-w-[70%]">
                    <span className="tw-font-normal tw-text-gray-700">{activeHeading.heading1}</span>
                    <span className="tw-font-medium tw-max-w-[80%] tw-text-gray-900">{typeof item[activeCategData?.key] === "string" ? getTitleCase(item[activeCategData?.key]) : item[activeCategData?.key]}</span>
                  </div>
                  {item[activeCategData.key1] !== "" ? (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <span className="tw-font-normal tw-text-gray-700">
                        {activeHeading.heading2} {activeCategory === "trade_country" && getTitleCase(item.key)}
                      </span>
                      <div className="tw-font-medium tw-text-gray-900">{commaFormat(item[activeCategData?.key1])}</div>
                    </div>
                  ) : (
                    "NA"
                  )}
                </div>
              )}
            </div>
          )
        );
      })}

      {category_count > 3 && renderPagination()}
    </div>
  ) : (
    <EmptyStateTradeOverview description="No Locations Found" />
  );
};

export default Locations;
